// Card trace — the client's half. After handling one agent:action envelope, tell the backend what
// the screen ACTUALLY did with it: applied, ignored (+ why), partial, or error. The api-service
// appends it to the turn's run as a `presentation_applied` agent step, so the agent-steps viewer
// shows when a client-side rule (e.g. workflowTurnIdsRef) overrode the backend's decision.
//
// "applied" means the envelope was DISPATCHED to the screen handlers without being skipped or
// throwing — list fetches/renders are async and are not awaited here.
//
// Contract (consumer.py `card.presentation_applied`): { run_id, message_id, result, reason,
// actions: [{ action_type, result, reason }] }. Unknown fields are ignored server-side.
import { socketService } from '@/services/SocketService';

export type AckResult = 'applied' | 'ignored' | 'partial' | 'error';

export interface ActionAck {
  action_type: string;
  result: Exclude<AckResult, 'partial'>;
  reason?: string;
}

export interface PresentationAck {
  type: 'card.presentation_applied';
  run_id: string;
  message_id?: string;
  /** The backend decision the envelope carried (presentation.decision), so the trace pairs them. */
  decision?: string;
  result: AckResult;
  reason?: string;
  actions: ActionAck[];
}

/** Collects per-action outcomes while one envelope is routed. Actions not noted count as applied. */
export class EnvelopeAck {
  private readonly notes = new Map<number, ActionAck>();

  constructor(private readonly actionTypes: string[]) {}

  /** Record what happened to the action at `index` (its position in the envelope's actions[]). */
  note(index: number, result: ActionAck['result'], reason?: string): void {
    if (index < 0 || index >= this.actionTypes.length) return;
    // First non-applied outcome wins — a later "applied" must not hide why an action was skipped.
    const prev = this.notes.get(index);
    if (prev && prev.result !== 'applied') return;
    this.notes.set(index, { action_type: this.actionTypes[index], result, ...(reason ? { reason } : {}) });
  }

  build(runId: string, messageId?: string, decision?: string): PresentationAck {
    const actions = this.actionTypes.map((action_type, i) => this.notes.get(i) ?? { action_type, result: 'applied' as const });
    const bad = actions.filter(a => a.result !== 'applied');
    const result: AckResult = actions.some(a => a.result === 'error') ? 'error'
      : bad.length === 0 ? 'applied'
        : bad.length === actions.length ? 'ignored' : 'partial';
    return {
      type: 'card.presentation_applied',
      run_id: runId,
      ...(messageId ? { message_id: messageId } : {}),
      ...(decision ? { decision } : {}),
      result,
      ...(bad[0]?.reason ? { reason: bad[0].reason } : {}),
      actions,
    };
  }
}

/** Fire-and-forget: an ack that fails to send must never affect the screen. */
export function sendPresentationAck(ack: PresentationAck): void {
  try {
    socketService.emit('message', ack);
  } catch {
    // tracing only
  }
}
